// Domain sheet — connect a custom domain to a finished app.
//
// A Pro feature: the user enters a domain, the gateway registers it with
// Cloudflare for SaaS (POST /v1/apps/:appId/domain) and returns the CNAME
// record to add at their DNS provider. Free users get a playful Pro
// upsell. The PreviewPanel opens this sheet from its "Connect domain"
// button.

import { useEffect, useState } from 'preact/hooks';
import { t } from '../lib/i18n';
import { createPortal } from 'preact/compat';
import type { JSX } from 'preact';
import { X, Globe, Lock, Check } from 'lucide-preact';
import { useAuth } from '@vibeshell/hooks';
import { getDeviceId } from '../lib/deviceId';
import type { RecentApp } from '../lib/recent-apps';

type Stage = 'idle' | 'connecting' | 'connected';

interface DnsRecord {
  type: string;
  name: string;
  target: string;
}

/** Friendly copy for the error codes POST /v1/apps/:appId/domain can return. */
function domainErrorCopy(code: unknown, message?: string): string {
  switch (code) {
    case 'invalid_domain':
      return t('dom.errInvalid');
    case 'domain_taken':
      return t('dom.errTaken');
    case 'not_configured':
      return message || t('dom.errNotConfigured');
    case 'forbidden':
    case 'not_found':
      return t('dom.errNotFound');
    case 'provision_failed':
      return t('dom.errProvision');
    case 'pro_required':
      return t('dom.errPro');
    default:
      return t('dom.errGeneric');
  }
}

export function DomainSheet({
  app,
  onClose
}: {
  app: RecentApp;
  onClose: () => void;
}): JSX.Element {
  const { session } = useAuth();
  const isPro = session.plan === 'pro';

  const [stage, setStage] = useState<Stage>('idle');
  const [domain, setDomain] = useState('');
  const [dns, setDns] = useState<DnsRecord | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const trimmed = domain.trim().toLowerCase();
  const looksValid = /^[^\s.]+(\.[^\s.]+)+$/.test(trimmed);

  async function connect(): Promise<void> {
    if (!looksValid) {
      setError(t('dom.errInvalid'));
      return;
    }
    setStage('connecting');
    setError('');
    try {
      const res = await fetch(`/v1/apps/${encodeURIComponent(app.appId)}/domain`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ domain: trimmed, deviceId: getDeviceId() })
      });
      const data = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
        message?: string;
        dns?: DnsRecord;
      };
      if (!res.ok || !data.ok || !data.dns) {
        setError(domainErrorCopy(data.error, data.message));
        setStage('idle');
        return;
      }
      setDns(data.dns);
      setStage('connected');
      window.posthog?.capture('app_domain_connected', { appId: app.appId });
    } catch {
      setError(t('dom.errReach'));
      setStage('idle');
    }
  }

  function goUpgrade(): void {
    onClose();
    location.hash = 'upgrade';
  }

  return createPortal(
    <div className="animate-vs-fade-in" style={overlay} onClick={onClose}>
      <div
        style={card}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t('dom.ariaConnect')}
      >
        <div style={grabHandle} aria-hidden="true" />
        <div style={head}>
          <span style={{ fontSize: 15, fontWeight: 700 }}>{t('dom.title')}</span>
          <button type="button" onClick={onClose} aria-label={t('common.close')} style={closeBtn}>
            <X size={16} />
          </button>
        </div>

        {!isPro ? (
          <div style={lockCard}>
            <span style={lockChip}>
              <Lock size={14} />
            </span>
            <p style={{ fontSize: 13.5, fontWeight: 700, margin: '10px 0 4px' }}>
              Custom domains are a Pro perk
            </p>
            <p style={{ ...bodyText, margin: '0 0 14px' }}>
              Put <strong style={{ color: 'var(--fg)' }}>{app.manifest.name}</strong> on your own
              domain, with an SSL certificate handled for you.
            </p>
            <button type="button" onClick={goUpgrade} style={primaryBtn}>
              Upgrade to Pro
            </button>
          </div>
        ) : stage === 'connected' && dns ? (
          <div>
            <div style={successRow}>
              <span style={checkChip}>
                <Check size={13} strokeWidth={3.5} />
              </span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 650 }}>{t('dom.registered')}</div>
                <div style={repoLink}>{dns.name}</div>
              </div>
            </div>
            <p style={{ ...bodyText, margin: '0 0 8px' }}>
              Add this record at your DNS provider:
            </p>
            <div style={dnsBox}>
              <DnsRow label="Type" value={dns.type} />
              <DnsRow label="Name" value={dns.name} />
              <DnsRow label="Target" value={dns.target} last />
            </div>
            <p style={hint}>
              Once the record propagates, Cloudflare verifies ownership and issues an SSL
              certificate automatically, this usually takes a few minutes.
            </p>
          </div>
        ) : (
          <div>
            <p style={bodyText}>
              Serve <strong style={{ color: 'var(--fg)' }}>{app.manifest.name}</strong> from a
              domain you own. Enter it below, you’ll get a CNAME record to add at your DNS
              provider.
            </p>
            <label style={fieldLabel}>{t('dom.yourDomain')}</label>
            <input
              type="text"
              autoComplete="off"
              spellcheck={false}
              value={domain}
              onInput={(e) => setDomain((e.currentTarget as HTMLInputElement).value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && looksValid && stage !== 'connecting') connect();
              }}
              placeholder="app.yoursite.com"
              style={field}
            />
            <button
              type="button"
              onClick={connect}
              disabled={stage === 'connecting' || !looksValid}
              style={{
                ...primaryBtn,
                opacity: stage === 'connecting' || !looksValid ? 0.6 : 1,
                cursor: stage === 'connecting' || !looksValid ? 'default' : 'pointer'
              }}
            >
              <Globe size={15} />
              {stage === 'connecting' ? t('dom.connecting') : t('dom.connect')}
            </button>
            {error && <p style={errLine}>{error}</p>}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

function DnsRow({
  label,
  value,
  last
}: {
  label: string;
  value: string;
  last?: boolean;
}): JSX.Element {
  return (
    <div style={last ? { ...dnsRow, borderBottom: 'none' } : dnsRow}>
      <span style={dnsLabel}>{label}</span>
      <span style={dnsValue}>{value}</span>
    </div>
  );
}

// ─── styles ─────────────────────────────────────────────────────────────────

const overlay: JSX.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 1000,
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  background: 'var(--scrim)',
  backdropFilter: 'var(--scrim-blur)'
};

const card: JSX.CSSProperties = {
  width: '100%',
  maxWidth: 440,
  maxHeight: '90dvh',
  overflowY: 'auto',
  background: 'var(--bg-elevated)',
  border: '1px solid var(--border)',
  borderBottom: 'none',
  borderTopLeftRadius: 'var(--radius-2xl)',
  borderTopRightRadius: 'var(--radius-2xl)',
  padding: '10px 20px calc(env(safe-area-inset-bottom) + 20px)',
  boxShadow: 'var(--shadow-lg)'
};

const grabHandle: JSX.CSSProperties = {
  width: 36,
  height: 4,
  borderRadius: 'var(--radius-full)',
  background: 'var(--border-strong)',
  margin: '0 auto 12px'
};

const head: JSX.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 14
};

const closeBtn: JSX.CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'var(--fg-muted)',
  cursor: 'pointer',
  padding: 4,
  display: 'grid',
  placeItems: 'center'
};

const bodyText: JSX.CSSProperties = {
  fontSize: 13.5,
  lineHeight: 1.55,
  color: 'var(--fg-muted)',
  margin: '0 0 16px'
};

const primaryBtn: JSX.CSSProperties = {
  width: '100%',
  padding: '12px 16px',
  borderRadius: 'var(--radius-md)',
  border: 'none',
  background: 'var(--color-primary)',
  color: 'var(--color-on-primary)',
  fontSize: 14,
  fontWeight: 650,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8
};

const lockCard: JSX.CSSProperties = {
  textAlign: 'center',
  padding: '20px 16px',
  borderRadius: 14,
  background: 'var(--bg-subtle)',
  border: '1px solid var(--border)'
};

const lockChip: JSX.CSSProperties = {
  display: 'inline-grid',
  placeItems: 'center',
  width: 34,
  height: 34,
  borderRadius: 9999,
  background: 'var(--color-primary)',
  color: 'var(--color-on-primary)'
};

const fieldLabel: JSX.CSSProperties = {
  display: 'block',
  fontSize: 11.5,
  fontWeight: 650,
  color: 'var(--fg-muted)',
  margin: '0 0 5px'
};

const field: JSX.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '10px 12px',
  borderRadius: 10,
  border: '1px solid var(--border)',
  background: 'var(--bg)',
  color: 'var(--fg)',
  fontSize: 13.5,
  marginBottom: 12
};

const hint: JSX.CSSProperties = {
  fontSize: 11,
  lineHeight: 1.5,
  color: 'var(--fg-subtle)',
  margin: '12px 0 0'
};

const successRow: JSX.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '12px 14px',
  borderRadius: 12,
  background: 'var(--bg-subtle)',
  border: '1px solid var(--border)',
  marginBottom: 14
};

const checkChip: JSX.CSSProperties = {
  flexShrink: 0,
  width: 24,
  height: 24,
  borderRadius: 9999,
  background: 'var(--color-success)',
  color: 'var(--color-on-primary)',
  display: 'grid',
  placeItems: 'center'
};

const repoLink: JSX.CSSProperties = {
  fontSize: 12,
  color: 'var(--fg-muted)',
  fontFamily: "'SF Mono', ui-monospace, Menlo, Consolas, monospace",
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

const dnsBox: JSX.CSSProperties = {
  borderRadius: 10,
  background: 'var(--bg-subtle)',
  border: '1px solid var(--border)',
  overflow: 'hidden'
};

const dnsRow: JSX.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '9px 14px',
  borderBottom: '1px solid var(--border)'
};

const dnsLabel: JSX.CSSProperties = {
  flexShrink: 0,
  width: 54,
  fontSize: 11,
  fontWeight: 650,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--fg-subtle)'
};

const dnsValue: JSX.CSSProperties = {
  fontSize: 12.5,
  color: 'var(--fg)',
  fontFamily: "'SF Mono', ui-monospace, Menlo, Consolas, monospace",
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

const errLine: JSX.CSSProperties = {
  fontSize: 12.5,
  color: 'var(--color-danger)',
  margin: '12px 0 0'
};
